import * as React from 'react'
import { 
  Box,
  Button,
  Stack,
  Popover,
  MenuList,
  MenuItem,
  ListItemText,
} from '@mui/material'
import { styled } from '@mui/material/styles';

import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';

import { useDispatch } from 'react-redux';
import { setTitle, setColor, setType, setMode, removeVisualization } from '../../slices/visualization';

import * as svg from 'save-svg-as-png';
import {utils, writeFileXLSX} from 'xlsx'

import GraphBlock from './GraphBlock';
import DataTable from '../DataTable';
import TitleModal from '../../modals/TitleModal';
import ColorModal from '../../modals/ColorModal';
import ChartModal from '../../modals/ChartModal';
import PromptModal from '../../modals/PromptModal';
import QueryModal from '../../modals/QueryModal';

const SettingButton = styled(Button)({
  minWidth: 20,
  minHeight: 20,
  width: 20,
  height: 20,
  background: 'transparent',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: 'none',
  cursor: 'pointer',
  color: '#8B8B8B',
  '&:hover': {
    background: '#F6F6F6'
  }
})

const SettingContainer = styled(Box)({
  border: '1px solid #E8E8E8',
  padding: '0.25rem 0.5rem',
  borderRadius: '0.25rem',
  '&:hover': {
    border: '1px solid #BFBFBF'
  }
})

const SettingPopOver = styled(Popover)({
  '>.MuiPaper-root': {
    boxShadow: '1px 1px 12px rgba(115, 115, 115, 0.2)',
    border: '1px solid #E8E8E8',
    borderRadius: '0.5rem',
    paddingLeft: '0.25rem',
    paddingRight: '0.25rem',

    '>.MuiList-root': {
      paddingBottom: '0.25rem',
      paddingTop: '0.25rem',
      '>.MuiMenuItem-root': {
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        '>.MuiListItemText-primary': {
          fontSize: '0.75rem',
          fontWeight: 300,
          lineHeight: 1.2
        }
      }
    }
  }
})

const GridItem = ({
  item,
  title,
  prompt,
  data,
  type,
  color,
  query,
  children,
  mode,
  ...rest
}) => {
  const divRef = React.useRef(null);
  const dispatch = useDispatch();

  const [dimensions, setDimensions] = React.useState({width: 0, height: 0});

  const [openTitle, setOpenTitle] = React.useState(false);
  const [openPrompt, setOpenPrompt] = React.useState(false);
  const [openColor, setOpenColor] = React.useState(false);
  const [openChart, setOpenChart] = React.useState(false);
  const [openQuery, setOpenQuery] = React.useState(false);

  // const [mode, setMode] = React.useState('chart');

  const [anchorDownloadEl, setAnchorDownloadEl] = React.useState(null);
  const [anchorDetailEl, setAnchorDetailEl] = React.useState(null);

  React.useEffect(() => {
    const currentDiv = divRef.current;

    const updateDimensions = (entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);

    if (currentDiv) {
      resizeObserver.observe(currentDiv);
    }

    // Clean up the observer on component unmount
    return () => {
      if (currentDiv) {
        resizeObserver.unobserve(currentDiv);
      }
    };
  }, []);

  const handleDownloadMenu = (e) => {
    setAnchorDownloadEl(e.currentTarget);
  }

  const handleDetailMenu = (e) => {
    setAnchorDetailEl(e.currentTarget)
  }

  const handleDownloadClose = () => {
    setAnchorDownloadEl(null);
  };

  const handleDetailClose = () => {
    setAnchorDetailEl(null);
  };

  const handleExportImage = (e) => {
    setAnchorDownloadEl(null);
    const svgElements = document.getElementById(item).getElementsByTagName("svg");
    if(svgElements.length === 0) return;
    svg.saveSvgAsPng(svgElements[0], `chart.png`)
  }

  const handleExportCsv = (e) => {
    const headers = Object.keys(data[0]);
    let dummyData = "";
    headers.forEach((item, index) => {
      dummyData+=item;
      if(index !== headers.length-1)
        dummyData+=','
    });
    dummyData+="\n";

    data.forEach(contextItem => {
      let newLine = "";
      headers.forEach((column, index) => {
        newLine+=contextItem[column];
        if(index !== headers.length-1)
          newLine+=",";
      })
      newLine+="\n";
      dummyData+=newLine;
    })

    const csvContent = `data:text/csv;charset=utf-8,${dummyData}`;
    const encodedURI = encodeURI(csvContent);
    window.open(encodedURI);
    setAnchorDownloadEl(null);
  }

  const handleExportExcel = (e) => {
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Data");
    writeFileXLSX(wb, `test_xlsx.xlsx`);
    setAnchorDownloadEl(null);
  }

  const handleExportGooglesheet = (e) => {
    setAnchorDownloadEl(null);
  }

  const handleEditTitle = (e) => {
    setOpenTitle(true);
    setAnchorDetailEl(null);
  }

  const handleEditPrompt = (e) => {
    setOpenPrompt(true);
    setAnchorDetailEl(null);
  }

  const handleEditColor = (e) => {
    setOpenColor(true);
    setAnchorDetailEl(null);
  }

  const handleEditChart = (e) => {
    setOpenChart(true);
    setAnchorDetailEl(null);
  }

  const handleViewSql = (e) => {
    setOpenQuery(true);
    setAnchorDetailEl(null);
  }

  const handleRemove = (e) => {
    dispatch(removeVisualization({item}))
    setAnchorDetailEl(null);
  }

  const handleClickTable = (e) => {
    dispatch(setMode({mode: 'table', item}));
  }

  const handleClickChart = (e) => {
    dispatch(setMode({mode: 'graph', item}));
  }

  const handleSaveTitle = (value) => {
    dispatch(setTitle({item, title: value}))
    setOpenTitle(false);
  }

  const handleCloseTitle = (e) => {
    setOpenTitle(false);
  }  

  const handleSavePrompt = (value) => {
    setOpenPrompt(false);
  }
  
  const handleClosePrompt = (e) => {
    setOpenPrompt(false);
  }

  const handleSaveColor = (value) => {
    dispatch(setColor({item, color: value}))
    setOpenColor(false);
  }

  const handleCloseColor = (e) => {
    setOpenColor(false);
  }  

  const handleSaveChart = (value) => {
    dispatch(setType({item, type: value}))
    setOpenChart(false);
  }

  const handleCloseChart = (e) => {
    setOpenChart(false);
  }

  const handleCloseQuery = (e) => {
    setOpenQuery(false);
  }

  const openDownload = Boolean(anchorDownloadEl);
  const downloadId = openDownload ? 'download-popover' : undefined;

  const openDetail = Boolean(anchorDetailEl);
  const detailId = openDownload ? 'detail-popover' : undefined;
  
  return (
    <Box className={`grid-item`} style={{height: '100%'}} item={item} {...rest} ref={divRef}>
      <Box className="grid-item__title dashboard-item-header">
        <Box className='dashboard-item-title'>{title}</Box>
        <Stack direction={"row"} spacing={1} sx={{padding: '0.5rem'}}>
          <SettingContainer>
            <Stack direction={"row"} spacing={1}>
              <SettingButton onClick={handleClickChart}>
                <AutoGraphOutlinedIcon fontSize='smaller' />
              </SettingButton>
              <SettingButton onClick={handleClickTable}>
                <TableViewOutlinedIcon fontSize='smaller' />
              </SettingButton>
            </Stack>
          </SettingContainer>
          <SettingContainer onClick={handleDownloadMenu}>
            <SettingButton>
              <FileDownloadOutlinedIcon fontSize='smaller' />
            </SettingButton>
          </SettingContainer>
          <SettingContainer onClick={handleDetailMenu}>
            <SettingButton>
              <MoreVertOutlinedIcon fontSize='smaller' />
            </SettingButton>
          </SettingContainer>
        </Stack>
      </Box>
      <Box className="grid-item__graph" id={item}>
        {
          mode === 'graph' ? (
            <GraphBlock type={type} data={data} width={dimensions.width-10} height={dimensions.height-40} color={color} />
          ) : (
            <DataTable data={data} width={dimensions.width-10} height={dimensions.height-56} />
          )
        }
      </Box>
      <SettingPopOver
        id={downloadId}
        open={openDownload}
        anchorEl={anchorDownloadEl}
        onClose={handleDownloadClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          marginTop: 1
        }}
        disableScrollLock
      >
        <MenuList dense>
          <MenuItem onClick={handleExportImage}>
            <ListItemText>Export insight as image</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportCsv}>
            <ListItemText>Export data as csv</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportExcel}>
            <ListItemText>Export data as excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportGooglesheet}>
            <ListItemText>Export data as googlesheet</ListItemText>
          </MenuItem>
        </MenuList>
      </SettingPopOver>
      <SettingPopOver
        id={detailId}
        open={openDetail}
        anchorEl={anchorDetailEl}
        onClose={handleDetailClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          marginTop: 1
        }}
        disableScrollLock
      >
        <MenuList dense>
          <MenuItem onClick={handleEditTitle}>
            <ListItemText>Edit Title</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditPrompt}>
            <ListItemText>Edit Prompt</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditColor}>
            <ListItemText>Edit Color</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditChart}>
            <ListItemText>Edit Chart</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleViewSql}>
            <ListItemText>View SQL</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleRemove}>
            <ListItemText>Remove</ListItemText>
          </MenuItem>
        </MenuList>
      </SettingPopOver>
      <TitleModal 
        open={openTitle}
        value={title}
        handleSave={handleSaveTitle}
        handleClose={handleCloseTitle}
      />
      <PromptModal 
        open={openPrompt}
        value={prompt}
        id={item}
        title={title}
        type={type}
        color={color}
        handleSave={handleSavePrompt}
        handleClose={handleClosePrompt}
      />
      <ColorModal 
        open={openColor}
        value={color}
        handleSave={handleSaveColor}
        handleClose={handleCloseColor}
      />
      <ChartModal 
        open={openChart}
        type={type}
        handleSave={handleSaveChart}
        handleClose={handleCloseChart}
      />
      <QueryModal
        open={openQuery}
        query={query}
        handleClose={handleCloseQuery}
      />
      {children}
    </Box>
  );
};

export default GridItem;